import { Link } from "react-router-dom";
import { useState } from "react";
import {
  ArrowRight, ArrowUpRight, Check, FileText, Home as HomeIcon, Building2,
  ShieldCheck, MessageSquare, Clock3, SlidersHorizontal, Layers3, Wallet,
  BadgeCheck, CircleHelp, ChevronDown, BookOpen,
} from "lucide-react";
import { LANDING } from "@/constants/testIds";
import CaseStudies from "@/components/site/CaseStudies";
import ProjectsSlider from "@/components/site/ProjectsSlider";
import LeadDialog from "@/components/configurator/LeadDialog";
import SEO from "@/components/site/SEO";

const MEDIA = "/bfv-media-20260916";
const SHARE_IMAGE = "https://bandofotovoltaico24.it/api/files/50549085-de37-4564-a7f3-6404925ffdeb";
const imageSet = (name) => [480, 800, 1200].map(w => MEDIA + "/" + name + "-" + w + ".webp " + w + "w").join(", ");

const checks = [
  { icon: SlidersHorizontal, title: "Taglia adatta", summary: "La potenza è coerente con i tuoi consumi e orari?", text: "Confrontiamo la potenza proposta con i consumi e gli orari in cui usi energia. Più grande non significa sempre più adatto." },
  { icon: Layers3, title: "Componenti chiari", summary: "Modelli e caratteristiche da chiarire.", text: "Leggiamo modelli di pannelli, inverter e batteria, caratteristiche dichiarate e compatibilità da verificare." },
  { icon: Wallet, title: "Costi completi", summary: "Voci incluse, esclusioni e costi da chiarire.", text: "Mettiamo a fuoco voci incluse, esclusioni e costi accessori. Il totale, da solo, non racconta tutta l'offerta." },
  { icon: BadgeCheck, title: "Garanzie leggibili", summary: "Prodotto, prestazioni e assistenza: tre coperture diverse.", text: "Distinguiamo garanzie di prodotto, prestazioni e assistenza: cosa coprono, a quali condizioni e chi interviene." },
  { icon: MessageSquare, title: "Domande da fare", summary: "Cosa chiarire con l'installatore prima di firmare.", text: "Ti indichiamo cosa chiarire con l'installatore prima della firma, spiegando perché conta nel tuo caso." },
];

const questions = [
  ["L'analisi è davvero gratuita?", "Sì. L'analisi del preventivo è gratuita e non comporta obblighi di acquisto. Un eventuale contatto con installatori o professionisti è separato e facoltativo: avviene solo se lo richiedi o presti l'apposito consenso."],
  ["Cosa devo avere a disposizione?", "Il preventivo completo e, se disponibili, i consumi degli ultimi 12 mesi, le fasce orarie e le schede dei componenti proposti. Nel modulo iniziale puoi descrivere l'offerta e i tuoi dubbi: ti ricontattiamo per concordare come raccogliere i documenti necessari."],
  ["Quando riceverò una risposta?", "Rispondiamo solitamente entro 24 ore per una prima presa in carico. I tempi dell'analisi completa vengono concordati in base ai documenti disponibili e alle verifiche necessarie: non promettiamo una valutazione completa in 24 ore."],
  ["Mi direte anche se non conviene procedere?", "Se dai dati emergono una potenza poco coerente, costi da chiarire o elementi che rendono dubbia la convenienza, te li segnaliamo e ne spieghiamo le ragioni. L'obiettivo è aiutarti a decidere, anche ridimensionando la proposta o approfondendo prima di impegnarti."],
  ["L'analisi sostituisce un sopralluogo o un progetto?", "No. È una lettura documentale e orientativa, non una certificazione, un progetto esecutivo o una verifica della copertura. Sicurezza, strutture, connessione e fattibilità richiedono le verifiche dei professionisti competenti sul caso reale."],
  ["Bandi e incentivi sono già inclusi nel risparmio?", "No. Non consideriamo un incentivo automaticamente disponibile. Beneficiario, requisiti, data della spesa e disponibilità della misura vanno verificati separatamente. Bandofotovoltaico24 è un servizio privato: non è un ente pubblico e non assegna contributi."],
  ["I miei recapiti verranno passati ad altri?", "L'analisi non richiede il consenso al marketing né al contatto con partner. I consensi sono separati e facoltativi. Per finalità, modalità di trattamento e diritti puoi consultare l'informativa privacy collegata al modulo."],
];

function Photo({ name, alt, width, height, sizes, className = "" }) {
  return <img src={MEDIA + "/" + name + "-800.webp"} srcSet={imageSet(name)} sizes={sizes}
    alt={alt} width={width} height={height} loading="lazy" decoding="async" className={className} />;
}

/* ─── Hero SVG Illustration ─────────────────────────────────────────────── */
function HeroIllustration() {
  const sunAngles = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];
  return (
    <svg
      role="img"
      aria-labelledby="hero-il-t hero-il-d"
      viewBox="0 0 500 412"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      className="w-full h-auto"
    >
      <title id="hero-il-t">Schema illustrativo: casa con pannelli, sole, inverter, batteria e preventivo</title>
      <desc id="hero-il-d">Casa con tetto fotovoltaico collegata a inverter e batteria. Sole in alto a destra. Documento preventivo in primo piano. Tre callout: Consumi, Componenti, Costi inclusi.</desc>
      <defs>
        <linearGradient id="heroSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fef3c7" stopOpacity="0.55" />
          <stop offset="70%" stopColor="#fdf9f2" />
          <stop offset="100%" stopColor="#e8f5ee" />
        </linearGradient>
        <radialGradient id="heroSun" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="55%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
        <filter id="hs" x="-15%" y="-15%" width="130%" height="130%">
          <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="#0d3325" floodOpacity="0.11" />
        </filter>
        <filter id="ds" x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="2" dy="4" stdDeviation="6" floodColor="#0d3325" floodOpacity="0.14" />
        </filter>
        <style>{`
          @media (prefers-reduced-motion: reduce) {
            #heroDot, #heroDot * { display: none; }
          }
          @keyframes heroPulse {
            0%,100% { opacity:0; }
            20%,70% { opacity:0.95; }
          }
        `}</style>
      </defs>

      {/* Sky */}
      <rect width="500" height="412" fill="url(#heroSky)" />
      {/* Ground */}
      <ellipse cx="250" cy="308" rx="240" ry="28" fill="#d6ead0" opacity="0.5" />
      <rect x="0" y="300" width="500" height="112" fill="#e5efe2" />

      {/* SUN */}
      {sunAngles.map((a, i) => {
        const rad = a * Math.PI / 180;
        const cx = 430, cy = 68;
        return <line key={i}
          x1={cx + 42 * Math.cos(rad)} y1={cy + 42 * Math.sin(rad)}
          x2={cx + 58 * Math.cos(rad)} y2={cy + 58 * Math.sin(rad)}
          stroke="#f59e0b" strokeWidth="2.2" strokeLinecap="round" opacity="0.65" />;
      })}
      <circle cx="430" cy="68" r="33" fill="url(#heroSun)" />
      <circle cx="430" cy="68" r="20" fill="#fde68a" opacity="0.3" />

      {/* HOUSE */}
      <g filter="url(#hs)">
        {/* Chimney */}
        <rect x="274" y="115" width="18" height="38" rx="2" fill="#3d6b50" />
        {/* Roof */}
        <polygon points="112,200 248,108 384,200" fill="#1a4a38" />
        <line x1="112" y1="200" x2="384" y2="200" stroke="#0d3325" strokeWidth="1.8" />
        {/* House body */}
        <rect x="130" y="198" width="236" height="120" fill="#fef8f0" rx="2" />
        {/* Subtle siding */}
        {[218, 236, 254, 272, 290].map(y => (
          <line key={y} x1="130" y1={y} x2="366" y2={y} stroke="#e8ddd2" strokeWidth="0.5" opacity="0.5" />
        ))}
        {/* Left window */}
        <rect x="147" y="220" width="58" height="46" fill="#cce4f5" rx="3" />
        <rect x="147" y="220" width="58" height="46" fill="none" stroke="#99c8e8" strokeWidth="1.2" rx="3" />
        <line x1="176" y1="220" x2="176" y2="266" stroke="#99c8e8" strokeWidth="1" />
        <line x1="147" y1="243" x2="205" y2="243" stroke="#99c8e8" strokeWidth="1" />
        {/* Right window */}
        <rect x="290" y="220" width="58" height="46" fill="#cce4f5" rx="3" />
        <rect x="290" y="220" width="58" height="46" fill="none" stroke="#99c8e8" strokeWidth="1.2" rx="3" />
        <line x1="319" y1="220" x2="319" y2="266" stroke="#99c8e8" strokeWidth="1" />
        <line x1="290" y1="243" x2="348" y2="243" stroke="#99c8e8" strokeWidth="1" />
        {/* Door */}
        <rect x="227" y="256" width="48" height="62" fill="#c4956a" rx="3" />
        <rect x="227" y="256" width="48" height="62" fill="none" stroke="#a07850" strokeWidth="1" rx="3" />
        <circle cx="268" cy="288" r="3.5" fill="#7a5830" />
        <line x1="227" y1="280" x2="275" y2="280" stroke="#a07850" strokeWidth="0.7" opacity="0.6" />
      </g>

      {/* SOLAR PANELS on left slope (4 parallelograms) */}
      {[
        [114, 184, 138, 168, 146, 180, 122, 196],
        [141, 165, 165, 149, 173, 161, 149, 177],
        [168, 146, 192, 130, 200, 142, 176, 158],
        [195, 127, 219, 111, 227, 123, 203, 139],
      ].map((pts, i) => (
        <g key={i}>
          <polygon
            points={pts.join(",")}
            fill="#0a2e1f"
            stroke="#2a6a4a"
            strokeWidth="0.9"
          />
          {/* Grid lines */}
          <line
            x1={(pts[0] + pts[2]) / 2} y1={(pts[1] + pts[3]) / 2}
            x2={(pts[6] + pts[4]) / 2} y2={(pts[7] + pts[5]) / 2}
            stroke="#2a6a4a" strokeWidth="0.5" opacity="0.7"
          />
          <line
            x1={(pts[0] + pts[6]) / 2} y1={(pts[1] + pts[7]) / 2}
            x2={(pts[2] + pts[4]) / 2} y2={(pts[3] + pts[5]) / 2}
            stroke="#2a6a4a" strokeWidth="0.5" opacity="0.5"
          />
        </g>
      ))}

      {/* INVERTER */}
      <g filter="url(#hs)">
        <rect x="366" y="230" width="56" height="42" rx="5" fill="#e8f5ee" stroke="#2a6a4a" strokeWidth="1.5" />
        <text x="394" y="248" textAnchor="middle" fontSize="7.5" fill="#1a4a38" fontWeight="bold" fontFamily="sans-serif">INV</text>
        {[0, 1, 2].map(i => (
          <circle key={i} cx={379 + i * 12} cy={260} r="3.5" fill="none" stroke="#2a6a4a" strokeWidth="1" />
        ))}
      </g>
      <text x="394" y="285" textAnchor="middle" fontSize="8" fill="#4d7060" fontFamily="sans-serif">Inverter</text>

      {/* BATTERY */}
      <g filter="url(#hs)">
        <rect x="432" y="212" width="46" height="68" rx="5" fill="#d4ebe0" stroke="#2a6a4a" strokeWidth="1.5" />
        <rect x="440" y="206" width="30" height="9" rx="3" fill="#2a6a4a" />
        <rect x="437" y="264" width="34" height="8" rx="2" fill="#2a6a4a" opacity="0.75" />
        <rect x="437" y="252" width="34" height="8" rx="2" fill="#2a6a4a" opacity="0.5" />
        <rect x="437" y="240" width="34" height="8" rx="2" fill="#2a6a4a" opacity="0.25" />
      </g>
      <text x="455" y="292" textAnchor="middle" fontSize="8" fill="#4d7060" fontFamily="sans-serif">Batteria</text>

      {/* ENERGY FLOW lines */}
      {/* Sun → panels */}
      <path d="M 400 82 Q 360 105 310 138" stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.5" />
      {/* Panels → inverter */}
      <path id="epPath" d="M 220 145 Q 285 175 330 220 Q 355 242 366 245" stroke="#2a6a4a" strokeWidth="1.3" strokeDasharray="5,3" opacity="0.45" />
      {/* Inverter ↔ battery */}
      <line x1="422" y1="248" x2="432" y2="248" stroke="#2a6a4a" strokeWidth="1.3" opacity="0.55" />
      {/* Inverter → house (consumption amber) */}
      <path d="M 366 252 Q 350 258 345 278 Q 342 298 338 316" stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.45" />

      {/* DOCUMENT */}
      <g filter="url(#ds)" transform="rotate(-6, 80, 285)">
        <rect x="24" y="252" width="94" height="114" rx="5" fill="white" stroke="#2a6a4a" strokeWidth="1.5" />
        <polygon points="98,252 118,272 98,272" fill="#e8f5ee" stroke="#2a6a4a" strokeWidth="1" />
        <line x1="98" y1="252" x2="118" y2="272" stroke="#2a6a4a" strokeWidth="1.2" />
        <rect x="34" y="268" width="54" height="7" rx="2" fill="#1a4a38" opacity="0.7" />
        {[0, 1, 2, 3, 4, 5].map(i => (
          <rect key={i} x="34" y={283 + i * 12} width={i % 2 === 0 ? 58 : 46} height="5" rx="1.5" fill="#c5d9c2" opacity="0.65" />
        ))}
        <rect x="34" y="343" width="48" height="5" rx="1.5" fill="#f59e0b" opacity="0.5" />
      </g>
      <text x="71" y="375" textAnchor="middle" fontSize="8.5" fill="#617469" fontFamily="sans-serif">Preventivo</text>

      {/* CALLOUT: Consumi → house body */}
      <path d="M 175 248 Q 100 242 80 232" stroke="#94c5a8" strokeWidth="1" strokeDasharray="3,2" opacity="0.9" />
      <rect x="0" y="218" width="82" height="26" rx="5" fill="#f0faf4" stroke="#94c5a8" strokeWidth="1" />
      <text x="41" y="234" textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#112f29" fontFamily="sans-serif">Consumi</text>

      {/* CALLOUT: Componenti → panels */}
      <path d="M 172 148 Q 170 106 190 96" stroke="#94c5a8" strokeWidth="1" strokeDasharray="3,2" opacity="0.9" />
      <rect x="150" y="76" width="114" height="26" rx="5" fill="#f0faf4" stroke="#94c5a8" strokeWidth="1" />
      <text x="207" y="92" textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#112f29" fontFamily="sans-serif">Componenti</text>

      {/* CALLOUT: Costi inclusi → document */}
      <path d="M 88 338 Q 92 352 106 358" stroke="#94c5a8" strokeWidth="1" strokeDasharray="3,2" opacity="0.9" />
      <rect x="20" y="354" width="118" height="26" rx="5" fill="#f0faf4" stroke="#94c5a8" strokeWidth="1" />
      <text x="79" y="370" textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#112f29" fontFamily="sans-serif">Costi inclusi</text>

      {/* Badge */}
      <rect x="152" y="356" width="140" height="20" rx="10" fill="white" stroke="#c5d9c2" strokeWidth="1" opacity="0.9" />
      <text x="222" y="370" textAnchor="middle" fontSize="8.5" fill="#617469" fontFamily="sans-serif" fontWeight="500">Schema illustrativo</text>

      {/* Animated energy dot */}
      <g id="heroDot">
        <circle r="4.5" fill="#f59e0b" style={{ animation: "heroPulse 2.8s ease-in-out infinite" }}>
          <animateMotion dur="2.8s" repeatCount="indefinite">
            <mpath href="#epPath" xlinkHref="#epPath" />
          </animateMotion>
        </circle>
      </g>
    </svg>
  );
}

/* ─── Landing ────────────────────────────────────────────────────────────── */
export default function Landing() {
  const [guideLead, setGuideLead] = useState({ open: false, slug: null, tipo: "privato" });
  const openGuideLead = (slug) => setGuideLead({ open: true, slug, tipo: slug === "aziende" ? "azienda" : "privato" });
  const closeGuideLead = () => setGuideLead(s => ({ ...s, open: false }));

  return (
    <main className="home-v2 bg-[#0a211d] text-[#d1e2dc]">
      <SEO title="Analisi gratuita preventivo fotovoltaico | Bandofotovoltaico24"
        description="Prima di firmare, confronta il preventivo fotovoltaico con i tuoi consumi: analisi gratuita e personalizzata di dimensionamento, componenti, prezzo e garanzie."
        path="/" image={SHARE_IMAGE} />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section
        data-testid={LANDING.hero}
        className="pt-28 pb-12 md:pt-36 md:pb-18 overflow-hidden bg-[#102b27]"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Text column */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[#79dfb6] text-xs font-semibold tracking-wide mb-6 px-3 py-1.5 rounded-full border border-[#79dfb6]/30 bg-[#173d35]">
              <span className="w-2 h-2 rounded-full bg-[#79dfb6] shrink-0" aria-hidden="true" />
              CONSULENZA GRATUITA · IN TUTTA ITALIA
            </div>
            <h1 className="font-display font-extrabold text-[2.6rem] sm:text-5xl lg:text-[3.6rem] leading-[1.06] tracking-tight max-w-2xl text-white">
              Il fotovoltaico giusto<br className="hidden sm:block" />
              <span className="text-[#79dfb6]"> parte da te.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[#d1e2dc] text-base md:text-lg leading-relaxed">
              Hai un preventivo? Ti aiutiamo a capire se rispetta i tuoi consumi, le tue esigenze e il tuo investimento.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <Link to="/analisi-preventivo" data-testid={LANDING.ctaAnalisi} className="inline-flex min-h-12 items-center gap-2 px-6 py-3 rounded-lg bg-[#2dde98] text-[#071e18] font-bold text-sm hover:bg-[#25c285] transition-colors duration-200">
                Fai valutare il preventivo <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <Link to="/configuratore" data-testid={LANDING.ctaConfigura} className="inline-flex min-h-11 items-center gap-2 text-sm text-[#79dfb6] underline underline-offset-4 decoration-[#79dfb6]/40 hover:decoration-[#79dfb6] transition-colors duration-200">
                Non hai un&apos;offerta? Simula l&apos;impianto <ArrowUpRight size={15} className="shrink-0" aria-hidden="true" />
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2.5 text-sm text-[#a0c4b8]">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={15} aria-hidden="true" className="text-[#79dfb6]" /> Consulenza gratuita, senza obbligo di acquisto</span>
              <span className="inline-flex items-center gap-2"><Clock3 size={15} aria-hidden="true" className="text-[#79dfb6]" /> Primo riscontro solitamente entro 24 ore</span>
            </div>
          </div>

          {/* Hero photo */}
          <figure className="lg:col-span-5">
            <div className="w-full aspect-[3/2] overflow-hidden rounded-2xl shadow-lg">
              <img
                src="/hero-1200.webp"
                srcSet="/hero-600.webp 600w, /hero-1200.webp 1200w"
                sizes="(min-width: 1024px) 480px, (min-width: 768px) 50vw, calc(100vw - 48px)"
                alt="Tetto fotovoltaico su abitazione — immagine illustrativa"
                width={1200}
                height={800}
                loading="eager"
                fetchPriority="high"
                className="w-full h-full object-cover"
              />
            </div>
            <figcaption className="mt-2 text-center text-xs text-[#a0c4b8]">Immagine illustrativa generata con AI</figcaption>
          </figure>
        </div>
      </section>

      {/* ── COSA RICEVI (manifesto) ───────────────────────────────────────── */}
      <section id="manifesto" data-testid="landing-manifesto" className="bg-[#173d35] py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-2xl mb-10">
            <p className="overline text-[#79dfb6] mb-3">Cosa ricevi</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight text-white">Cinque verifiche.<br />Una scelta più consapevole.</h2>
            <p className="mt-4 text-[#a0c4b8] leading-relaxed">Indicazioni comprensibili, punti da approfondire e domande motivate.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {checks.map((item) => (
              <article key={item.title} className="bg-[#1a4a38] rounded-xl border border-[#2a6a4a] p-5 hover:border-[#79dfb6]/60 hover:shadow-sm transition-all duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-[#102b27] border border-[#2a6a4a] flex items-center justify-center">
                    <item.icon size={17} className="text-[#79dfb6]" aria-hidden="true" />
                  </div>
                  <h3 className="font-display font-bold text-lg leading-tight text-white">{item.title}</h3>
                </div>
                <details>
                  <summary className="flex items-start gap-2 cursor-pointer select-none">
                    <span aria-hidden="true" className="shrink-0 text-[#79dfb6] font-bold text-base leading-5 mt-0.5 transition-transform duration-200">+</span>
                    <span className="text-sm text-[#a0c4b8] leading-relaxed">{item.summary}</span>
                  </summary>
                  <p className="mt-3 text-sm text-[#a0c4b8] leading-relaxed border-t border-[#2a6a4a] pt-3">{item.text}</p>
                </details>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3 PASSI (come-funziona) ──────────────────────────────────────── */}
      <section id="come-funziona" className="bg-[#0a211d] py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-xl mb-10">
            <p className="overline text-[#79dfb6] mb-3">Come funziona</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight text-[#f5faf7]">Dal dubbio<br />a una scelta più chiara.</h2>
          </div>
          <ol className="grid md:grid-cols-3 gap-0 md:gap-0 divide-y md:divide-y-0 md:divide-x divide-[#2a6a4a]/40">
            {[
              { n: "01", icon: FileText, title: "Mandaci la proposta", text: "Compila il modulo con l'offerta ricevuta e i dubbi che vuoi risolvere.", color: "#1a4a38" },
              { n: "02", icon: BookOpen, title: "La leggiamo sul tuo caso", text: "Ti ricontattiamo solitamente entro 24 ore per raccogliere preventivo, consumi e documenti.", color: "#1a4a38" },
              { n: "03", icon: Check, title: "Chiarisci prima di firmare", text: "Ricevi indicazioni motivate su cosa appare coerente e cosa verificare prima di decidere.", color: "#1a4a38" },
            ].map((step) => (
              <li key={step.n} className="group py-7 md:py-0 md:px-8 first:md:pl-0 last:md:pr-0 flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 shrink-0 rounded-full bg-emerald-900 text-white text-sm font-bold flex items-center justify-center">{step.n}</span>
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-[#1b4038] border border-[#2a6a4a] flex items-center justify-center">
                    <step.icon size={17} className="text-[#79dfb6]" aria-hidden="true" />
                  </div>
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl mb-2 text-[#f5faf7]">{step.title}</h3>
                  <p className="text-sm text-[#adc7bd] leading-relaxed">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-10">
            <Link to="/analisi-preventivo" className="btn-primary min-h-12 w-fit">
              Inizia l&apos;analisi gratuita <ArrowRight size={17} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CASA / AZIENDA ───────────────────────────────────────────────── */}
      <section className="bg-[#173d35] py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-2xl mb-9">
            <p className="overline text-[#79dfb6] mb-3">Stesso metodo, esigenze diverse</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight text-[#f5faf7]">Prima la tua vita.<br />Poi i pannelli.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { id: "privati", narrative: "privati-narrativa", testId: LANDING.privatiCard, name: "privati", icon: HomeIcon, kind: "Per la tua casa", title: "Un impianto che segua le tue abitudini.", alt: "Villetta con pannelli fotovoltaici", height: 498, text: "Consumi serali, pompa di calore, ricarica dell'auto, spazio sul tetto. Analizziamo ciò che conta per te, senza dare per scontato che servano più pannelli o una batteria.", link: "/configuratore/privato", cta: "Simula la mia casa" },
              { id: "aziende", narrative: "aziende-narrativa", testId: LANDING.aziendeCard, name: "aziende", icon: Building2, kind: "Per la tua impresa", title: "Un investimento legato al lavoro reale.", alt: "Capannone industriale con impianto fotovoltaico", height: 533, text: "Orari di attività, macchinari, stagionalità e copertura. Leggiamo il preventivo rispetto ai consumi dell'impresa e distinguiamo energia utilizzata, costi accessori e agevolazioni da verificare.", link: "/configuratore/azienda", cta: "Simula il capannone" },
            ].map(item => (
              <article id={item.id} data-testid={item.testId} key={item.id} className="rounded-2xl overflow-hidden bg-[#1b4038] border border-[#2a6a4a] hover:shadow-md transition-shadow duration-200">
                <div className="aspect-[16/9] overflow-hidden">
                  <Photo name={item.name} alt={item.alt} width={800} height={item.height}
                    sizes="(min-width: 1280px) 580px, (min-width: 768px) calc((100vw - 72px) / 2), calc(100vw - 48px)"
                    className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500" />
                </div>
                <div id={item.narrative} className="p-6 md:p-8">
                  <p className="inline-flex gap-2 items-center overline text-[#79dfb6] mb-3"><item.icon size={16} aria-hidden="true" />{item.kind}</p>
                  <h3 className="font-display font-bold text-2xl md:text-3xl leading-tight text-[#f5faf7]">{item.title}</h3>
                  <p className="text-[#adc7bd] leading-relaxed mt-3 mb-6 text-base">{item.text}</p>
                  <Link to="/analisi-preventivo" className="btn-primary min-h-12">Analizza il mio preventivo <ArrowRight size={16} aria-hidden="true" /></Link>
                  <Link to={item.link} className="mt-3 flex items-center gap-2 min-h-11 text-sm font-medium text-[#79dfb6] underline underline-offset-4 hover:text-[#a0c4b8] transition-colors duration-200">{item.cta} <ArrowUpRight size={15} aria-hidden="true" /></Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── GUIDE PDF ────────────────────────────────────────────────────── */}
      <section id="guide" data-testid="landing-guides" className="bg-[#102b27] py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-9 items-start">
          <div className="lg:col-span-4">
            <p className="overline text-[#79dfb6] mb-3">Da leggere con calma</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight text-[#f5faf7]">Prima di firmare,<br />riconosci la qualità.</h2>
            <p className="mt-4 text-[#adc7bd] leading-relaxed text-sm">Due guide di 19 pagine illustrate: componenti, posa, sicurezza e documenti. Percorsi distinti per casa e capannone. Lasci i dati per ricevere la guida; i consensi promozionali restano facoltativi.</p>
            <Photo name="dettaglio" alt="Dettaglio dei moduli fotovoltaici" width={480} height={270}
              sizes="(min-width: 1024px) 350px, (min-width: 640px) 480px, calc(100vw - 48px)"
              className="mt-6 w-full max-w-sm aspect-video object-cover rounded-xl" />
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              { slug: "privati", title: "Fotovoltaico per la casa", testId: LANDING.guidePrivati, icon: HomeIcon, coverLabel: "Guida casa", text: "Dal preventivo al tetto: capisci che cosa rende un impianto adatto alle tue abitudini e ben realizzato.", points: ["Pannelli, batteria, fissaggi e protezioni", "Pratiche, verifiche e documenti da chiedere", "Sei domande da inviare all'installatore"] },
              { slug: "aziende", title: "Fotovoltaico per aziende e capannoni", testId: LANDING.guideAziende, icon: Building2, coverLabel: "Guida impresa", text: "Consumi e turni, copertura e gestione dell'impianto: valuta l'offerta rispetto al lavoro reale della tua impresa.", points: ["Qualità della posa e condizioni del tetto", "Connessione, antincendio e controlli: quando si applicano", "Documenti, assistenza e confronto delle offerte"] },
            ].map(item => (
              <button type="button" key={item.slug} data-testid={item.testId} onClick={() => openGuideLead(item.slug)}
                className="flex flex-col text-left rounded-xl bg-[#1b4038] border border-[#2a6a4a] p-6 hover:border-[#79dfb6] hover:shadow-sm transition-all duration-200">
                {/* CSS mockup cover */}
                <div className="relative mb-5 rounded-lg overflow-hidden h-24 w-full">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0d3325] to-[#1a5c3e]" />
                  <div className="absolute top-3 right-3 opacity-10">
                    <item.icon size={44} className="text-white" />
                  </div>
                  <div className="absolute inset-0 flex flex-col justify-end p-4">
                    <p className="text-emerald-200 text-xs font-bold uppercase tracking-widest">{item.coverLabel}</p>
                    <div className="mt-2 flex flex-col gap-1">
                      {[64, 50, 58].map((w, i) => (
                        <div key={i} className="h-1 rounded-full bg-white/25" style={{ width: w }} />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="overline text-[#adc7bd] mb-2">19 pagine illustrate · PDF gratuito</p>
                <h3 className="font-display font-bold text-xl mb-3 text-[#f5faf7]">{item.title}</h3>
                <p className="text-[#adc7bd] text-sm leading-relaxed">{item.text}</p>
                <ul className="mt-4 space-y-2 text-sm text-[#adc7bd]">
                  {item.points.map(point => (
                    <li key={point} className="flex items-start gap-2">
                      <Check size={14} className="mt-0.5 shrink-0 text-[#79dfb6]" aria-hidden="true" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                <span className="mt-auto pt-6 flex items-center gap-2 font-semibold text-sm text-[#79dfb6]">
                  Scarica la guida gratuita <ArrowRight size={15} aria-hidden="true" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── GUIDE ONLINE ─────────────────────────────────────────────────── */}
      <section aria-labelledby="guida-online-title" className="bg-[#0a211d] py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex flex-col gap-4">
          {[
            {
              id: "guida-online-title",
              icon: BookOpen,
              label: "Guida confronto preventivi",
              title: "Due preventivi, una scelta da capire.",
              desc: "Cinque controlli e una scheda di confronto per scoprire cosa include ogni offerta.",
              linkTo: "/guide/confrontare-preventivi-fotovoltaico",
              linkText: "Come confrontare i preventivi",
            },
            {
              id: null,
              icon: BookOpen,
              label: "Guida accumulo fotovoltaico",
              title: "Batteria inclusa: ma serve davvero?",
              desc: "Capacità utile, consumi serali e costi: le domande per scegliere l'accumulo sul tuo caso.",
              linkTo: "/guide/accumulo-fotovoltaico-quando-conviene",
              linkText: "Valuta la proposta con accumulo",
            },
          ].map((g, i) => (
            <article key={i} className="rounded-2xl bg-[#1b4038] border border-[#2a6a4a] p-6 md:p-9 grid md:grid-cols-12 gap-6 items-center hover:border-[#79dfb6]/60 transition-colors duration-200">
              <div className="md:col-span-8 flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 rounded-lg bg-[#102b27] border border-[#2a6a4a] flex items-center justify-center mt-0.5">
                  <g.icon size={18} className="text-[#79dfb6]" aria-hidden="true" />
                </div>
                <div>
                  <p className="overline text-[#79dfb6] mb-2">{g.label} · Lettura libera</p>
                  {g.id
                    ? <h2 id={g.id} className="font-display font-bold text-2xl md:text-3xl leading-tight text-[#f5faf7]">{g.title}</h2>
                    : <h2 className="font-display font-bold text-2xl md:text-3xl leading-tight text-[#f5faf7]">{g.title}</h2>
                  }
                  <p className="mt-2 text-[#adc7bd] leading-relaxed text-sm">{g.desc}</p>
                </div>
              </div>
              <div className="md:col-span-4">
                <Link to={g.linkTo} className="inline-flex min-h-12 items-center gap-2 font-semibold text-[#79dfb6] underline underline-offset-4 hover:text-[#a0c4b8] transition-colors duration-200">
                  {g.linkText} <ArrowRight size={17} className="shrink-0" aria-hidden="true" />
                </Link>
                <p className="mt-2 text-sm text-[#adc7bd]">Leggila qui, senza lasciare i dati.</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── CASI STUDIO ──────────────────────────────────────────────────── */}
      <div className="bg-[#061414] text-white"><CaseStudies /><ProjectsSlider /></div>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section id="incentivi" className="bg-[#173d35] py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <CircleHelp className="text-[#79dfb6] mb-5" size={28} aria-hidden="true" />
            <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight text-[#f5faf7]">Le risposte,<br />senza note nascoste.</h2>
            <p className="mt-5 text-[#adc7bd] leading-relaxed">Che cosa comprende il servizio, cosa serve e quali sono i suoi limiti.</p>
            <Link to="/privacy" className="inline-flex items-center min-h-11 mt-4 text-[#79dfb6] text-sm underline underline-offset-4">Leggi l&apos;informativa privacy</Link>
          </div>
          <div className="lg:col-span-8 divide-y divide-[#2a6a4a]/40 border-y border-[#2a6a4a]/40">
            {questions.map(([q, a]) => (
              <details key={q} className="py-5">
                <summary className="font-semibold text-base cursor-pointer min-h-11 flex items-center gap-3 text-[#f5faf7]">
                  <span className="text-[#79dfb6] text-xl" aria-hidden="true">+</span>{q}
                </summary>
                <p className="pt-3 pb-1 text-[#adc7bd] leading-relaxed text-sm md:text-base">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="bg-[#0a211d] py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="overline text-[#79dfb6] mb-3">Il prossimo passo</p>
            <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight text-[#f5faf7]">Hai un preventivo e un dubbio?<br />Partiamo da lì.</h2>
            <p className="text-[#adc7bd] mt-5 max-w-xl leading-relaxed">Non devi sapere già tutto sul fotovoltaico. Raccontaci la proposta: ti aiutiamo a capire cosa chiedere prima di decidere.</p>
          </div>
          <div className="md:col-span-4">
            <Link to="/analisi-preventivo" className="btn-primary min-h-12">Analizza il mio preventivo <ArrowRight size={17} aria-hidden="true" /></Link>
            <p className="mt-3 text-sm text-[#adc7bd] flex items-center gap-2"><Check size={16} aria-hidden="true" /> Gratuito e senza obbligo di acquisto</p>
          </div>
        </div>
      </section>

      <LeadDialog open={guideLead.open} onClose={closeGuideLead} tipo={guideLead.tipo} guideSlug={guideLead.slug} />
    </main>
  );
}
